import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuthContext } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { useProfessionalDetail } from '../../../hooks/useProfessionalDetail';
import { useAvailability } from '../../../hooks/useAvailability';
import { useBooking } from '../../../hooks/useBookings';

import { IBookingRequest, PatientInfoData } from '../../../types/Booking/booking';
import { localStorageHelper } from '../../../utils/localStorageHelper';
import { systemSettingsService } from '../../../services/systemSettingsService';
import { SystemSettings } from '../../../types';

export const useProfessionalPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { slug } = useParams<{ slug: string }>();
    const { logout, user } = useAuthContext();
    const { showNotification } = useNotification();

    const { professional, studies, loading: loadingProfessional, error } = useProfessionalDetail(slug || "");
    const { availability, loading: loadingAvailability } = useAvailability(professional?.profile?.uid || '');
    const { createBooking } = useBooking();

    const [systemSettings, setSystemSettings] = useState<SystemSettings | null>(null);

    const [isFavorite, setIsFavorite] = useState(false);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
    const [currentBookingUid, setCurrentBookingUid] = useState<string | null>(null);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const settings = await systemSettingsService.getSettings();
                setSystemSettings(settings);
            } catch (err) {
                console.error("Error loading system settings", err);
            }
        };
        fetchSettings();
    }, []);

    const handleToggleFavorite = () => setIsFavorite(prev => !prev);
    const handleBookingModalClose = () => setIsBookingModalOpen(false);
    const handleGoBack = () => navigate('/professionals');

    const handleConfirmBooking = async (patientInfo: PatientInfoData) => {
        const bookingInfo = localStorageHelper.getItem<IBookingRequest>('bookingDetails');

        try {
            const result = await createBooking({
                professional_user_uid: bookingInfo?.professional_user_uid ?? "",
                date_booking_start: bookingInfo?.date_booking_start ?? "",
                email: patientInfo.email,
                name: patientInfo.name,
                number: patientInfo.number,
                notes: bookingInfo?.notes,
                photo: patientInfo.photo,
                type: bookingInfo?.type,
                reserve_reason: patientInfo.reserveReason,
                quote_for_invited: patientInfo.quoteForInvited,
                consult_type: bookingInfo?.consult_type,
                session_type: bookingInfo?.session_type
            });

            if (result?.response) {
                const currentDetails = localStorageHelper.getItem<any>('bookingDetails') || {};
                const dateStart = currentDetails.date_booking_start || '';
                localStorageHelper.setItem('bookingDetails', {
                    ...currentDetails,
                    professionalName: (professional?.profile?.first_name || '') + ' ' + (professional?.profile?.last_name || ''),
                    selectedDate: dateStart ? dateStart.split(' ')[0] : '',
                    selectedTime: dateStart ? dateStart.split(' ')[1] : '',
                    price: professional?.profile?.details?.consult_price || 0,
                });

                localStorageHelper.setItem('booking', result.response);
                localStorageHelper.setItem('bookingUid', result.response.uid);
                setCurrentBookingUid(result.response.uid);
                setIsConfirmationModalOpen(true);
                handleBookingModalClose();
            }

            if (result?.error) {
                showNotification(result.error.message, 'error');
                if (result.error.code === 401) {
                    localStorageHelper.setItem<string>('redirectAfterLogin', window.location.pathname + window.location.search);
                    logout();
                }
            }
        } catch (err) {
            showNotification(t('landing.professionalsDetail.errorBooking'), 'error');
        }
    };

    return {
        professional,
        studies,
        availability,
        loading: loadingProfessional || loadingAvailability,
        error,
        slug,
        isFavorite,
        isBookingModalOpen,
        isConfirmationModalOpen,
        currentBookingUid,
        t,
        systemSettings,
        setIsBookingModalOpen,
        setIsConfirmationModalOpen,
        handleToggleFavorite,
        handleBookingModalClose,
        handleGoBack,
        handleConfirmBooking,
        user
    };
};